// Kanban der Ticket-Liste (design_handoff_tickets §1): eine Spalte je Status, Statuswechsel per Ziehen.
// Ziehlogik wie bisher (HTML5 Drag & Drop); Erledigt/Geschlossen fragt die Liste nach dem Abschlussgrund.
import React, { useRef, useState } from 'react';
import { DotsSixVertical, Fire } from '@phosphor-icons/react';
import { STATUS_REIHENFOLGE } from '../../../lib/tickets/darstellung';
import { KleinAvatar, TicketPrioPill, prioFarbe, statusFarbe, statusLabel } from './liste/teile';

interface Ticket {
  id: string;
  ticket_nummer: string;
  titel: string;
  status: string;
  prioritaet: string;
  eskaliert: boolean;
  objekt?: { bezeichnung: string } | null;
  zustaendig?: { full_name: string; isTeam?: boolean } | null;
}

interface TicketKanbanProps<T extends Ticket> {
  tickets: T[];
  onSelectTicket: (ticket: T) => void;
  onStatusChange?: (ticketId: string, newStatus: string) => void;
  selectedTicketId?: string;
}

function TicketKanban<T extends Ticket>({ tickets, onSelectTicket, onStatusChange, selectedTicketId }: TicketKanbanProps<T>) {
  const [draggedTicketId, setDraggedTicketId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const dragCounterRef = useRef<Record<string, number>>({});

  const handleDragStart = (e: React.DragEvent, ticket: T) => {
    setDraggedTicketId(ticket.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ticket.id);
  };

  const handleDragEnd = () => {
    setDraggedTicketId(null);
    setDragOverColumn(null);
    dragCounterRef.current = {};
  };

  const handleDragEnter = (e: React.DragEvent, columnKey: string) => {
    e.preventDefault();
    dragCounterRef.current[columnKey] = (dragCounterRef.current[columnKey] || 0) + 1;
    setDragOverColumn(columnKey);
  };

  const handleDragLeave = (e: React.DragEvent, columnKey: string) => {
    e.preventDefault();
    dragCounterRef.current[columnKey] = (dragCounterRef.current[columnKey] || 0) - 1;
    if (dragCounterRef.current[columnKey] <= 0) {
      dragCounterRef.current[columnKey] = 0;
      setDragOverColumn(prev => (prev === columnKey ? null : prev));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, columnKey: string) => {
    e.preventDefault();
    setDragOverColumn(null);
    dragCounterRef.current = {};
    const ticketId = e.dataTransfer.getData('text/plain');
    const ticket = tickets.find(t => t.id === ticketId);
    if (ticket && ticket.status !== columnKey && onStatusChange) onStatusChange(ticketId, columnKey);
    setDraggedTicketId(null);
  };

  const gezogen = tickets.find(t => t.id === draggedTicketId);

  return (
    <div className="immo-scroll flex overflow-x-auto" style={{ gap: 12, paddingBottom: 8 }}>
      {STATUS_REIHENFOLGE.map(key => {
        const farbe = statusFarbe(key);
        const spalte = tickets.filter(t => t.status === key);
        const ziel = dragOverColumn === key && !!draggedTicketId && gezogen?.status !== key;
        return (
          <section key={key} aria-label={statusLabel(key)}
            onDragEnter={e => handleDragEnter(e, key)} onDragLeave={e => handleDragLeave(e, key)} onDragOver={handleDragOver} onDrop={e => handleDrop(e, key)}
            className={`${ziel ? '' : 'immo-glas'} flex shrink-0 flex-col`}
            style={{ width: 272, borderRadius: 24, padding: 10, gap: 8, minHeight: 200, transition: 'background .2s, box-shadow .2s',
              ...(ziel ? { background: `color-mix(in srgb, ${farbe} 12%, var(--bg))`, boxShadow: `inset 0 0 0 2px ${farbe}` } : {}) }}>
            <header className="flex items-center justify-between" style={{ padding: '4px 6px 2px' }}>
              <span className="flex items-center" style={{ gap: 8, fontSize: 13, fontWeight: 800 }}>
                <span aria-hidden style={{ width: 9, height: 9, borderRadius: '50%', background: farbe }} />
                {statusLabel(key)}
              </span>
              <span className="tabular-nums" style={{ minWidth: 22, height: 20, padding: '0 7px', borderRadius: 10, fontSize: 11, fontWeight: 800, lineHeight: '20px', textAlign: 'center',
                background: `color-mix(in srgb, ${farbe} 14%, transparent)`, color: farbe }}>{spalte.length}</span>
            </header>
            {spalte.length > 0 ? spalte.map(ticket => (
              <div key={ticket.id} data-ticket-row draggable role="button" tabIndex={0}
                onDragStart={e => handleDragStart(e, ticket)} onDragEnd={handleDragEnd}
                onClick={() => onSelectTicket(ticket)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectTicket(ticket); } }}
                aria-label={`${ticket.ticket_nummer} ${ticket.titel} öffnen`}
                data-tip="Ticket öffnen – Detailansicht mit Verlauf und Einsatz"
                className="immo-tkarte flex flex-col"
                style={{ borderRadius: 16, padding: 12, gap: 8, background: 'var(--bg)', cursor: 'grab',
                  boxShadow: `inset 3px 0 0 ${prioFarbe(ticket.prioritaet)}, inset 0 0 0 1px var(--line)${selectedTicketId === ticket.id ? ', 0 0 0 2px var(--acc)' : ''}`,
                  opacity: draggedTicketId === ticket.id ? 0.4 : 1 }}>
                <span className="flex items-center justify-between">
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink3)' }}>{ticket.ticket_nummer}</span>
                  <span className="flex items-center" style={{ gap: 4, color: 'var(--ink3)' }}>
                    {ticket.eskaliert && <Fire size={12} weight="fill" color="#a24bc9" aria-label="Eskaliert" />}
                    <DotsSixVertical size={13} aria-hidden />
                  </span>
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ticket.titel}</span>
                {ticket.objekt && <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>{ticket.objekt.bezeichnung}</span>}
                <span className="flex items-center justify-between" style={{ gap: 8 }}>
                  <TicketPrioPill prio={ticket.prioritaet} />
                  {ticket.zustaendig && <KleinAvatar name={ticket.zustaendig.full_name} team={ticket.zustaendig.isTeam} groesse={22} />}
                </span>
              </div>
            )) : (
              <div className="flex flex-1 items-center justify-center" style={{ padding: '24px 0', fontSize: 12, fontWeight: 700, color: ziel ? farbe : 'var(--ink3)' }}>
                {ziel ? 'Hier ablegen' : 'Keine Tickets'}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export default TicketKanban;
